import { IDataDefinition } from "./IDataDefinition";
import { IDataDefinitionOwner } from "../IDataDefinitionOwner";
import { DataTypes } from "../DataTypes";
import { IConfigStorage } from "../../IConfigStorage";
import { ValidationIssue } from "../../IDesignValidationProvider";
import type { AppGlueAny } from "../../AppGlueAny";

export abstract class BaseDataDefinition<T> implements IDataDefinition, IConfigStorage {
    abstract readonly type: DataTypes;
    name: string = "";
    list: boolean = false;
    description?: string;
    valueIsDefault: boolean = false;
    owner?: IDataDefinitionOwner;

    constructor(name?: string) {
        if (name) this.name = name;
    }

    getValue(): T | undefined {
        if (this.name && this.owner) return this.owner.getValueObject()[this.name] as T;
        return;
    }

    setValue(value: T) {
        if (this.name && this.owner) {
            this.owner.getValueObject()[this.name] = value;
        }
    }

    getListValue(): T[] | undefined {
        if (this.name && this.owner) return this.owner.getValueObject()[this.name] as T[];
        return;
    }

    setListValue(value: T[]) {
        if (this.name && this.owner) this.owner.getValueObject()[this.name] = value;
    }

    getStorageData(): object {
        let object: object = {};

        Reflect.set(object, "__type", this.type);
        Reflect.set(object, "name", this.name);
        Reflect.set(object, "list", this.list);
        Reflect.set(object, "description", this.description);
        Reflect.set(object, "valueIsDefault", this.valueIsDefault);
        if (this.name && this.owner) {
            Reflect.set(object, "owner", this.owner);
        }
        return object;
    }

    setStorageData(data: object): void {
        this.name = Reflect.get(data, "name");
        this.list = Reflect.get(data, "list");
        this.description = Reflect.get(data, "description");
        this.valueIsDefault = Reflect.get(data, "valueIsDefault");
    }

    abstract verifyValueType(value: AppGlueAny): ValidationIssue | undefined;

    abstract convert(value: AppGlueAny): AppGlueAny;

    toJSONSchema(): object {
        if (this.list) {
            return {
                type: "array",
                items: { type: this.type },
            };
        }

        return {
            type: this.type,
        };
    }

    abstract toJSONObject(value: AppGlueAny): AppGlueAny;

    toStructureString(indent?: number): string {
        return `${" ".repeat(indent || 0)}${this.name}: ${this.type}${this.list ? " list" : ""}\n`;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (!this.name || this.name.trim() === "") issues.push(new ValidationIssue("Name is required"));

        return issues;
    }
}
